import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { PickupPointAdmin } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';

const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const dangerBtn =
  'rounded-md border border-admin-danger bg-white px-3 py-1.5 text-sm text-admin-danger hover:bg-admin-cream disabled:opacity-50';
const ghostBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-cream disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';

export default function AdminPickupPointsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<PickupPointAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    const response = await api.get<PickupPointAdmin[]>('/admin/pickup-points');
    setItems(response.data);
  };

  useEffect(() => {
    void (async () => {
      try {
        await load();
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar pontos de recolha'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleActive = async (point: PickupPointAdmin): Promise<void> => {
    setBusyId(point.id);
    try {
      await api.patch(`/admin/pickup-points/${point.id}`, {
        isActive: !point.isActive,
      });
      toast.success(point.isActive ? 'Ponto desativado.' : 'Ponto ativado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar ponto de recolha'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (point: PickupPointAdmin): Promise<void> => {
    if (
      !window.confirm(
        `Apagar o ponto de recolha "${point.name}"? Esta ação é irreversível.`
      )
    ) {
      return;
    }
    setBusyId(point.id);
    try {
      await api.delete(`/admin/pickup-points/${point.id}`);
      toast.success('Ponto de recolha eliminado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao apagar ponto de recolha'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="text-admin-text-soft">A carregar…</p>;

  return (
    <>
      <SEO title="Pontos de recolha (admin)" noindex />
      <div className="mb-4 flex items-center justify-between gap-2">
        <h1>Pontos de recolha</h1>
        <Link to="/admin/pontos-de-recolha/novo" className={primaryBtn}>
          Novo ponto de recolha
        </Link>
      </div>
      <p className="mb-6 text-sm text-admin-text-soft">
        Locais onde os clientes podem levantar a encomenda gratuitamente. Pontos
        inativos não aparecem no checkout.
      </p>

      {items.length === 0 ? (
        <p className="rounded-md border border-admin-border bg-white p-6 text-sm text-admin-text-soft">
          Ainda não existem pontos de recolha.{' '}
          <Link to="/admin/pontos-de-recolha/novo" className="text-admin-primary hover:underline">
            Criar o primeiro
          </Link>
          .
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-admin-border bg-white">
          <table className="w-full">
            <thead className="bg-admin-cream">
              <tr>
                <th className={thClass}>Foto</th>
                <th className={thClass}>Nome</th>
                <th className={thClass}>Morada</th>
                <th className={thClass}>Dias</th>
                <th className={thClass}>Estado</th>
                <th className={thClass}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-admin-border align-middle">
                  <td className="p-3">
                    {p.imageThumbUrl ? (
                      <img
                        src={p.imageThumbUrl}
                        alt={p.name}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <span className="text-xs text-admin-muted">—</span>
                    )}
                  </td>
                  <td className="p-3 text-sm font-medium">{p.name}</td>
                  <td className="p-3 text-sm text-admin-text-soft" style={{ maxWidth: 360 }}>
                    {p.address}
                  </td>
                  <td className="p-3 text-sm">{p.daysToCollect}</td>
                  <td className="p-3 text-sm">
                    {p.isActive ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                        Ativo
                      </span>
                    ) : (
                      <span className="rounded-full bg-admin-cream px-2 py-0.5 text-xs text-admin-muted">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={ghostBtn}
                        onClick={() => navigate(`/admin/pontos-de-recolha/${p.id}`)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className={ghostBtn}
                        disabled={busyId === p.id}
                        onClick={() => void toggleActive(p)}
                      >
                        {p.isActive ? 'Desativar' : 'Ativar'}
                      </button>
                      <button
                        type="button"
                        className={dangerBtn}
                        disabled={busyId === p.id}
                        onClick={() => void remove(p)}
                      >
                        Apagar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
